import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plus, Ticket, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api, type ModeloChamado } from '../api/cliente.js';
import { AcessoNegado, Carregando, ErroSeguro, Vazio } from '../componentes/estados.js';
import { useSessao } from '../sessao.js';

/**
 * Configuração da automação de chamados.
 *
 * Duas coisas precisam existir para o fluxo funcionar: a credencial do portal e
 * ao menos um modelo. A tela deixa isso explícito em vez de o usuário descobrir
 * na primeira tentativa frustrada.
 */
export function Automacao() {
  const { ehAdmin } = useSessao();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['automacao'],
    queryFn: () => api.automacao(),
  });

  if (!ehAdmin) return <AcessoNegado />;
  if (isLoading) return <Carregando />;
  if (isError || !data) return <ErroSeguro aoTentarNovamente={() => void refetch()} />;

  return (
    <div className="max-w-[760px]">
      <header className="mb-4">
        <h1 className="text-[length:var(--text-tela)] font-semibold text-texto">Automação</h1>
        <p className="mt-0.5 text-[length:var(--text-secundario)] text-texto-secundario">
          Configuração do fluxo de chamados. O seletor de qual automação está ativa fica em{' '}
          <strong>Conexão</strong>.
        </p>
      </header>

      <Credencial credencial={data.credencial} />
      <Modelos modelos={data.modelos} />
    </div>
  );
}

function Credencial({
  credencial,
}: {
  credencial: {
    usuario: string;
    urlPortal: string;
    atualizadaEm: string;
    ultimoUsoEm: string | null;
    ultimoErro: string | null;
  } | null;
}) {
  const cliente = useQueryClient();
  const [editando, setEditando] = useState(credencial === null);
  const [usuario, setUsuario] = useState(credencial?.usuario ?? '');
  const [senha, setSenha] = useState('');
  const [urlPortal, setUrlPortal] = useState(credencial?.urlPortal ?? '');

  const salvar = useMutation({
    mutationFn: () => api.salvarCredencialPortal({ usuario, senha, urlPortal }),
    onSuccess: async () => {
      setSenha('');
      setEditando(false);
      await cliente.invalidateQueries({ queryKey: ['automacao'] });
    },
  });

  return (
    <section className="mb-4 rounded-[var(--radius-card)] border border-borda bg-superficie p-5 shadow-[var(--shadow-card)]">
      <div className="mb-3 flex items-center gap-2">
        <KeyRound size={16} className="text-texto-terciario" aria-hidden />
        <h2 className="font-semibold text-texto">Credencial do portal</h2>
      </div>

      {credencial && !editando ? (
        <>
          <dl className="grid gap-2 text-[length:var(--text-secundario)] sm:grid-cols-2">
            <div>
              <dt className="text-texto-terciario">Usuário</dt>
              <dd className="mono text-texto">{credencial.usuario}</dd>
            </div>
            <div>
              <dt className="text-texto-terciario">Portal</dt>
              <dd className="truncate text-texto">{credencial.urlPortal}</dd>
            </div>
            <div>
              <dt className="text-texto-terciario">Atualizada em</dt>
              <dd className="mono text-texto">
                {new Date(credencial.atualizadaEm).toLocaleString('pt-BR')}
              </dd>
            </div>
            <div>
              <dt className="text-texto-terciario">Último uso</dt>
              <dd className="mono text-texto">
                {credencial.ultimoUsoEm
                  ? new Date(credencial.ultimoUsoEm).toLocaleString('pt-BR')
                  : 'nunca'}
              </dd>
            </div>
          </dl>

          {credencial.ultimoErro && (
            <p className="mt-3 rounded-[var(--radius-controle)] bg-saida-bg px-3 py-2 text-[length:var(--text-secundario)] text-saida-fg">
              Último erro: {credencial.ultimoErro}
            </p>
          )}

          <button
            type="button"
            onClick={() => setEditando(true)}
            className="mt-4 rounded-[var(--radius-controle)] border border-borda-input px-3 py-2 text-[length:var(--text-secundario)] text-texto-secundario hover:border-primaria hover:text-primaria"
          >
            Trocar credencial
          </button>
        </>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            salvar.mutate();
          }}
        >
          <Campo
            rotulo="Usuário"
            valor={usuario}
            aoMudar={setUsuario}
            placeholder="dominio\\usuario"
            mono
          />
          <Campo
            rotulo="Senha"
            valor={senha}
            aoMudar={setSenha}
            tipo="password"
            placeholder="••••••••"
          />
          <Campo
            rotulo="URL do portal"
            valor={urlPortal}
            aoMudar={setUrlPortal}
            placeholder="https://portal.exemplo.com.br/entrada"
          />

          {/* A senha é o dado mais sensível do sistema. Dizer o que acontece
              com ela reduz a chance de alguém guardá-la em outro lugar. */}
          <p className="mt-2 text-[length:var(--text-metadado)] text-texto-terciario">
            🔒 A senha é cifrada antes de ser gravada e nunca é exibida de volta — nem para você.
          </p>

          {salvar.isError && (
            <p className="mt-3 rounded-[var(--radius-controle)] bg-saida-bg px-3 py-2 text-[length:var(--text-secundario)] text-saida-fg">
              {salvar.error instanceof Error ? salvar.error.message : 'Não foi possível salvar.'}
            </p>
          )}

          <div className="mt-4 flex gap-2">
            <button
              type="submit"
              disabled={salvar.isPending || !usuario || !senha || !urlPortal}
              className="rounded-[var(--radius-controle)] bg-primaria px-4 py-2 text-[length:var(--text-secundario)] font-medium text-white hover:bg-primaria-hover disabled:opacity-45"
            >
              {salvar.isPending ? 'Salvando...' : 'Salvar'}
            </button>
            {credencial && (
              <button
                type="button"
                onClick={() => setEditando(false)}
                className="rounded-[var(--radius-controle)] border border-borda-input px-4 py-2 text-[length:var(--text-secundario)] text-texto-secundario"
              >
                Cancelar
              </button>
            )}
          </div>
        </form>
      )}
    </section>
  );
}

function Modelos({ modelos }: { modelos: ModeloChamado[] }) {
  const cliente = useQueryClient();
  const [criando, setCriando] = useState(false);

  const excluir = useMutation({
    mutationFn: (id: string) => api.excluirModeloChamado(id),
    onSuccess: async () => cliente.invalidateQueries({ queryKey: ['automacao'] }),
  });

  return (
    <section className="rounded-[var(--radius-card)] border border-borda bg-superficie p-5 shadow-[var(--shadow-card)]">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Ticket size={16} className="text-texto-terciario" aria-hidden />
          <h2 className="font-semibold text-texto">Modelos de chamado ({modelos.length})</h2>
        </div>
        <button
          type="button"
          onClick={() => setCriando((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-[var(--radius-controle)] border border-borda-input px-3 py-1.5 text-[length:var(--text-secundario)] text-texto-secundario hover:border-primaria hover:text-primaria"
        >
          <Plus size={13} aria-hidden />
          Novo
        </button>
      </div>

      {criando && <FormularioModelo aoConcluir={() => setCriando(false)} />}

      {modelos.length === 0 && !criando ? (
        <Vazio
          titulo="Nenhum modelo cadastrado"
          orientacao="Sem modelo, o sistema não sabe qual formulário preencher no portal."
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {modelos.map((m) => (
            <li
              key={m.id}
              className="flex items-start gap-3 rounded-[var(--radius-controle)] border border-borda-sutil p-3"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium text-texto">{m.nome}</p>
                {m.descricaoModelo && (
                  <p className="text-[length:var(--text-secundario)] text-texto-secundario">
                    {m.descricaoModelo}
                  </p>
                )}
                <p className="mt-1 text-[length:var(--text-metadado)] text-texto-terciario">
                  Gatilhos: {m.gatilhos.join(', ') || '—'} · usado {m.usos}×
                </p>
                <p className="mono mt-0.5 text-[length:var(--text-metadado)] text-texto-terciario">
                  {m.navegacao.join(' › ')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => excluir.mutate(m.id)}
                aria-label={`Excluir ${m.nome}`}
                className="shrink-0 rounded-[var(--radius-controle)] p-1.5 text-texto-terciario hover:bg-saida-bg hover:text-saida"
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function FormularioModelo({ aoConcluir }: { aoConcluir: () => void }) {
  const cliente = useQueryClient();
  const [nome, setNome] = useState('');
  const [descricaoModelo, setDescricao] = useState('');
  const [gatilhos, setGatilhos] = useState('');
  const [navegacao, setNavegacao] = useState('');
  const [localidade, setLocalidade] = useState('goias');
  const [detalhe, setDetalhe] = useState('sede');

  const criar = useMutation({
    mutationFn: () =>
      api.criarModeloChamado({
        nome,
        descricaoModelo: descricaoModelo || null,
        gatilhos: gatilhos
          .split(',')
          .map((g) => g.trim())
          .filter(Boolean),
        // Um passo por linha: o caminho de cliques no portal, em ordem.
        navegacao: navegacao
          .split('\n')
          .map((n) => n.trim())
          .filter(Boolean),
        camposFixos: { localidade, detalheLocalidade: detalhe },
      }),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['automacao'] });
      aoConcluir();
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        criar.mutate();
      }}
      className="mb-4 rounded-[var(--radius-controle)] border border-borda-input bg-fundo p-4"
    >
      <Campo rotulo="Nome" valor={nome} aoMudar={setNome} placeholder="Orientação ao usuário" />
      <Campo
        rotulo="Sobre o que é"
        valor={descricaoModelo}
        aoMudar={setDescricao}
        placeholder="Dúvidas e orientações sobre equipamentos e acessos"
      />
      <Campo
        rotulo="Gatilhos (separados por vírgula)"
        valor={gatilhos}
        aoMudar={setGatilhos}
        placeholder="orientação, dúvida, senha, ajuda"
      />

      <label className="mt-3 block">
        <span className="text-[length:var(--text-coluna)] font-semibold uppercase tracking-[0.05em] text-texto-terciario">
          Navegação no portal — um passo por linha
        </span>
        <textarea
          value={navegacao}
          onChange={(e) => setNavegacao(e.target.value)}
          rows={3}
          placeholder={'Equipments\nI have a problem\nComputers, Printers, and'}
          className="mono mt-1 w-full rounded-[var(--radius-controle)] border border-borda-input bg-superficie p-2 text-[length:var(--text-secundario)] text-texto placeholder:text-[var(--cor-placeholder)]"
        />
        <span className="mt-1 block text-[length:var(--text-metadado)] text-texto-terciario">
          Exatamente os rótulos dos botões, na ordem em que você clica.
        </span>
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <Campo rotulo="Busca da localidade" valor={localidade} aoMudar={setLocalidade} mono />
        <Campo rotulo="Busca do detalhe" valor={detalhe} aoMudar={setDetalhe} mono />
      </div>

      {criar.isError && (
        <p className="mt-3 rounded-[var(--radius-controle)] bg-saida-bg px-3 py-2 text-[length:var(--text-secundario)] text-saida-fg">
          {criar.error instanceof Error ? criar.error.message : 'Não foi possível criar.'}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          disabled={criar.isPending || !nome || !navegacao.trim()}
          className="rounded-[var(--radius-controle)] bg-primaria px-4 py-2 text-[length:var(--text-secundario)] font-medium text-white hover:bg-primaria-hover disabled:opacity-45"
        >
          {criar.isPending ? 'Criando...' : 'Criar modelo'}
        </button>
        <button
          type="button"
          onClick={aoConcluir}
          className="rounded-[var(--radius-controle)] border border-borda-input px-4 py-2 text-[length:var(--text-secundario)] text-texto-secundario"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}

function Campo({
  rotulo,
  valor,
  aoMudar,
  tipo = 'text',
  placeholder,
  mono = false,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (v: string) => void;
  tipo?: string;
  placeholder?: string;
  mono?: boolean;
}) {
  return (
    <label className="mt-3 block">
      <span className="text-[length:var(--text-coluna)] font-semibold uppercase tracking-[0.05em] text-texto-terciario">
        {rotulo}
      </span>
      <input
        type={tipo}
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        placeholder={placeholder ?? ''}
        className={`mt-1 h-10 w-full rounded-[var(--radius-controle)] border border-borda-input bg-superficie px-3 text-[length:var(--text-corpo)] text-texto placeholder:text-[var(--cor-placeholder)] ${
          mono ? 'mono' : ''
        }`}
      />
    </label>
  );
}
